import "firebase/auth";
import { Store, AnyAction } from "redux";
import thunk, { ThunkMiddleware } from "redux-thunk";
import { History } from "history";

import { definedMap, withDefault } from "collection-utils";

import { RootState, AppsAndState, AppAndState } from "./types";
import { rootState } from "../reducers/RootState";
import { SerializedApp } from "common/components/SerializedApp";
import * as appsActions from "../actions/apps-actions";
import { MaterialGlideAppState } from "webapp/chrome/smart/material-glide-app/material-glide-app";
import { subscribeTablesState } from "../listeners/tables-state-listeners";
import * as ReduxTK from "@reduxjs/toolkit";
import { defaultRootState } from "reducers/default-root-state";

// Should the components be included in the Redux dev tools state?
const withComponents = false;

function sanitizeSerializedApp(app: SerializedApp): SerializedApp;
function sanitizeSerializedApp(app: Partial<SerializedApp>): Partial<SerializedApp>;
function sanitizeSerializedApp(app: Partial<SerializedApp>): Partial<SerializedApp> {
    const functions = withComponents ? withDefault(app.functions, {}) : {};
    return { ...app, functions };
}

function sanitizeAppState(state: Partial<MaterialGlideAppState>): any {
    if (state.rootContext === undefined) {
        return "no-root-context";
    }
    return "has-root-context";
}

function sanitizeAppAndState(app: AppAndState): AppAndState;
function sanitizeAppAndState(app: Partial<AppAndState>): Partial<AppAndState>;
function sanitizeAppAndState(app: Partial<AppAndState>): Partial<AppAndState> {
    return {
        ...app,
        appState: definedMap(app.appState, sanitizeAppState),
        serializedApp: definedMap(app.serializedApp, (s: SerializedApp) => sanitizeSerializedApp(s)),
    };
}

function stateSanitizer(state: RootState): Partial<RootState> {
    const apps: AppsAndState = {};
    for (const id of Object.keys(state.apps)) {
        const app = state.apps[id];
        if (app === "loading") {
            apps[id] = app;
        } else {
            apps[id] = sanitizeAppAndState(app);
        }
    }
    return { ...state, apps };
}

function actionSanitizer(action: AnyAction): AnyAction {
    switch (action.type) {
        case appsActions.ActionType.UpdateSerializedApp: {
            const payload = action.payload as appsActions.UpdateSerializedAppPayload;
            return { ...action, payload: { ...payload, data: sanitizeSerializedApp(payload.data) } };
        }

        case appsActions.ActionType.SetApp: {
            const payload = action.payload as appsActions.SetAppPayload;
            return {
                ...action,
                payload: {
                    ...payload,
                    data: sanitizeAppAndState(payload.data),
                },
            };
        }

        case appsActions.ActionType.UpdateApp: {
            const payload = action.payload as appsActions.UpdateAppPayload;
            return {
                ...action,
                payload: {
                    ...payload,
                    data: {
                        ...payload.data,
                        updates: sanitizeAppAndState(payload.data.updates),
                    },
                },
            };
        }

        case appsActions.ActionType.UpdateGlideAppState: {
            const payload = action.payload as appsActions.UpdateGlideAppStatePayload;
            return {
                ...action,
                payload: {
                    ...payload,
                    data: undefined,
                },
            };
        }

        default:
            return action;
    }
}

let store: ReduxTK.EnhancedStore<RootState, AnyAction, ThunkMiddleware<RootState, AnyAction, History<any>>[]>;

export type AppDispatch = typeof store.dispatch;

export function configureStore(history: History): Store<RootState> {
    if (store !== undefined) {
        return store;
    }

    store = ReduxTK.configureStore({
        reducer: rootState,
        middleware: [thunk.withExtraArgument(history)],
        preloadedState: defaultRootState,
        devTools: {
            stateSanitizer: stateSanitizer as any,
            actionSanitizer: actionSanitizer as any,
        },
    });

    subscribeTablesState(store, history);

    return store;
}

export function configureTestStore(history: History, initialState: RootState): Store<RootState> {
    return ReduxTK.configureStore({
        reducer: rootState,
        middleware: [thunk.withExtraArgument(history)],
        preloadedState: initialState,
        devTools: {
            stateSanitizer: stateSanitizer as any,
            actionSanitizer: actionSanitizer as any,
        },
    });
}
